import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { WA_TEMPLATES, WA_VARS, fillTpl } from "@/lib/waTemplates";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, RotateCcw, Loader2, ChevronDown } from "lucide-react";
import { BirthdayImageField } from "@/components/BirthdayImageField";

const SAMPLE = { cliente: "Maria", nome: "Maria", placa: "ABC1D23", modelo: "Fiat Argo", link: "https://app.autooficina.com.br/…", valor: "R$ 450,00", data: "25/10/2026 às 09:00", status: "Em andamento", pix: "12.345.678/0001-90 (Oficina)" };

function TemplateRow({ t, value, onChange, shopName, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const pos = useRef(null);
  const insert = (v) => {
    const el = ref.current;
    const tag = `{${v}}`;
    const s = pos.current ?? value.length;
    onChange(value.slice(0, s) + tag + value.slice(s));
    pos.current = s + tag.length;
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(s + tag.length, s + tag.length); } });
  };
  const changed = value !== t.text;
  return (
    <div data-testid={`tpl-${t.key}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40">
      <button type="button" data-testid={`tpl-toggle-${t.key}`} onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="text-sm font-bold text-zinc-100">{t.label}{changed && <span className="ml-2 text-[10px] uppercase text-amber-300">personalizado</span>}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          <div className="flex flex-wrap gap-1">{[...WA_VARS, ...(t.extra || [])].map((v) => (
            <button key={v} type="button" data-testid={`tpl-var-${t.key}-${v}`} onClick={() => insert(v)} className="text-[11px] font-mono rounded-full border border-zinc-700 bg-zinc-950 px-2 py-0.5 text-green-neon hover:border-green-brand">{`{${v}}`}</button>
          ))}</div>
          <Textarea ref={ref} data-testid={`tpl-text-${t.key}`} value={value} onChange={(e) => { pos.current = e.target.selectionStart; onChange(e.target.value); }} onSelect={(e) => { pos.current = e.target.selectionStart; }} className="bg-zinc-950 border-zinc-800 text-sm min-h-[90px]" />
          <div className="rounded-lg bg-emerald-950/40 border border-emerald-900 p-3 text-sm text-zinc-200 whitespace-pre-wrap" data-testid={`tpl-preview-${t.key}`}>{fillTpl(value, { ...SAMPLE, oficina: shopName })}</div>
          <Button type="button" size="sm" variant="ghost" data-testid={`tpl-reset-${t.key}`} disabled={!changed} onClick={() => onChange(t.text)} className="text-zinc-400"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restaurar padrão</Button>
          {children}
        </div>
      )}
    </div>
  );
}

export function MessageTemplatesCard({ shop, onSaved }) {
  const [vals, setVals] = useState(() => Object.fromEntries(WA_TEMPLATES.map((t) => [t.key, shop?.wa_templates?.[t.key] || t.text])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const templates = Object.fromEntries(WA_TEMPLATES.filter((t) => vals[t.key].trim() && vals[t.key] !== t.text).map((t) => [t.key, vals[t.key]]));
      await api.put("/shop-settings/messages", { templates }); toast.success("Mensagens salvas"); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="message-templates-card" className="rounded-2xl border border-card2-border bg-card2 p-5 space-y-3">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase text-zinc-100"><MessageCircle className="h-4 w-4 text-green-brand" /> Mensagens de WhatsApp</h2>
      <p className="text-xs text-zinc-500">Esses textos são usados em todos os botões de WhatsApp do app. Toque numa variável para inserir no texto. Variável sem valor fica em branco.</p>
      <div className="space-y-2">{WA_TEMPLATES.map((t) => <TemplateRow key={t.key} t={t} shopName={shop?.name} value={vals[t.key]} onChange={(v) => setVals((p) => ({ ...p, [t.key]: v }))}>{t.key === "birthday" && <BirthdayImageField shop={shop} onSaved={onSaved} />}</TemplateRow>)}</div>
      <Button data-testid="tpl-save-btn" onClick={save} disabled={busy} className="bg-green-brand text-white font-bold uppercase">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar mensagens"}</Button>
    </section>
  );
}
